import GroupAddIcon from "@mui/icons-material/GroupAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useIntl } from "react-intl";
import { ImportMode } from "types/commonTypes";

const useStyles = (theme: Theme): { [key: string]: SxProps } => ({
  optionTitle: {
    fontSize: "1.0em",
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  disabledCard: {
    opacity: 0.5,
  },
});

type ImportUsersModeStepProps = {
  onSelect: (mode: ImportMode) => void;
  onClose: () => void;
  deactivateDisabledReasonId?: string;
};

/**
 * First step of the import wizard: add learners or deactivate them.
 *
 * Deactivation is offered only when the caller says it is available. An organization with no users has
 * nothing to deactivate, and a user count that failed to load is treated the same way — the option
 * stays closed rather than letting a destructive import start on unknown state.
 */
export function ImportUsersModeStep({
  onSelect,
  onClose,
  deactivateDisabledReasonId,
}: Readonly<ImportUsersModeStepProps>) {
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();

  const deactivateDisabled = !!deactivateDisabledReasonId;

  const deactivateCard = (
    <Card
      variant="outlined"
      sx={deactivateDisabled ? styles.disabledCard : undefined}
    >
      <CardActionArea
        disabled={deactivateDisabled}
        onClick={() => onSelect("deactivate")}
      >
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <PersonRemoveIcon color="error" />
            <Typography sx={styles.optionTitle}>
              {t({ id: "deactivateUsers" })}
            </Typography>
          </Box>
          <Typography variant="body2">
            {t({ id: "uploadAFileToDeactivateExistingUsers" })}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="body2">
          {t({ id: "chooseAnImportTypeToContinue" })}
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardActionArea onClick={() => onSelect("add")}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <GroupAddIcon color="primary" />
                  <Typography sx={styles.optionTitle}>
                    {t({ id: "addUsers" })}
                  </Typography>
                </Box>
                <Typography variant="body2">
                  {t({ id: "uploadAFileToAddNewUsers" })}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          {deactivateDisabled ? (
            <Tooltip title={t({ id: deactivateDisabledReasonId! })}>
              <Box>{deactivateCard}</Box>
            </Tooltip>
          ) : (
            deactivateCard
          )}
        </Grid>
      </Grid>
      <Grid container spacing={2} justifyContent="right" mt={2}>
        <Grid item xs={12} sm={12} md={2}>
          <Button onClick={onClose} fullWidth>
            {t({ id: "cancel" })}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
